// Live customer statement — always shows the current state, can be filtered
// by period and sent the same way invoices are sent.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Printer, MessageCircle, Mail, Copy, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatDate, formatMoney, toISODate } from '../lib/format';
import type { Contact, Invoice, Payment } from '../lib/types';

interface Line {
  date: string;
  doc: string;
  description: string;
  charge: number;   // increases balance
  credit: number;   // decreases balance
  running: number;
  link?: string;
}

export default function ContactStatement() {
  const { id } = useParams();
  const { currentOrg } = useOrg();
  const [contact, setContact] = useState<Contact | null>(null);
  const [from, setFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 3); return toISODate(d); });
  const [to, setTo] = useState(toISODate(new Date()));
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id || !currentOrg) return;
    supabase.from('dodobooks_contacts').select('*').eq('id', id).maybeSingle().then(({ data }) => setContact(data as Contact));
  }, [id, currentOrg]);

  useEffect(() => {
    if (!id || !currentOrg) return;
    Promise.all([
      supabase.from('dodobooks_invoices').select('*').eq('org_id', currentOrg.id).eq('contact_id', id).gte('issue_date', from).lte('issue_date', to).order('issue_date'),
      supabase.from('dodobooks_payments').select('*').eq('org_id', currentOrg.id).eq('contact_id', id).gte('payment_date', from).lte('payment_date', to).order('payment_date'),
    ]).then(([inv, pay]) => {
      setInvoices((inv.data ?? []) as Invoice[]);
      setPayments((pay.data ?? []) as Payment[]);
    });
  }, [id, currentOrg, from, to]);

  const lines: Line[] = useMemo(() => {
    type Item = { date: string; sort: number; line: Omit<Line, 'running'> };
    const items: Item[] = [];
    for (const i of invoices) {
      if (i.kind === 'invoice') items.push({
        date: i.issue_date, sort: 0,
        line: { date: i.issue_date, doc: i.number, description: i.reference || ('Invoice ' + i.number), charge: Number(i.total), credit: 0, link: '/invoices/' + i.id },
      });
      else items.push({
        date: i.issue_date, sort: 1,
        line: { date: i.issue_date, doc: i.number, description: 'Credit note ' + i.number, charge: 0, credit: Number(i.total), link: '/invoices/' + i.id },
      });
    }
    for (const p of payments.filter((x) => x.direction === 'received')) {
      items.push({
        date: p.payment_date, sort: 2,
        line: { date: p.payment_date, doc: p.reference || 'payment', description: 'Payment received' + (p.reference ? ' (' + p.reference + ')' : ''), charge: 0, credit: Number(p.amount) },
      });
    }
    items.sort((a, b) => a.date.localeCompare(b.date) || a.sort - b.sort);
    let running = 0;
    return items.map((it) => { running += it.line.charge - it.line.credit; return { ...it.line, running }; });
  }, [invoices, payments]);

  const totals = useMemo(() => ({
    charged: lines.reduce((s, l) => s + l.charge, 0),
    paid: lines.reduce((s, l) => s + l.credit, 0),
    balance: lines.length > 0 ? lines[lines.length - 1].running : 0,
  }), [lines]);

  async function copyAll() {
    const text = `Statement for ${contact?.name}\n${formatDate(from)} → ${formatDate(to)}\nBalance: ${formatMoney(totals.balance)}\n\n` +
      lines.map((l) => `${l.date}  ${l.doc.padEnd(16)}  ${l.description.padEnd(40)}  ${l.charge ? '+' + formatMoney(l.charge) : '-' + formatMoney(l.credit)}  bal ${formatMoney(l.running)}`).join('\n');
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  }

  if (!contact) return <div className="text-text-mute text-sm py-12 text-center">Loading…</div>;

  const reminderMessage = encodeURIComponent(`Hi ${contact.name},\n\nA quick reminder of your account balance with us: ${formatMoney(totals.balance)} as at ${formatDate(to)}.\n\nLet me know if anything looks off.\n\nThanks.`);
  const whatsappPhone = (contact.phone ?? '').replace(/[^\d]/g, '');

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div className="flex items-center gap-3">
          <Link to="/contacts" className="btn btn-ghost"><ArrowLeft size={14} /></Link>
          <div>
            <h1 className="h-page">Statement</h1>
            <p className="text-text-dim text-sm mt-1">{contact.name}{contact.email ? ' · ' + contact.email : ''}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => window.print()} className="btn btn-secondary"><Printer size={14} /> Print / PDF</button>
          <button onClick={copyAll} className="btn btn-secondary">{copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}</button>
          {contact.phone && (
            <a href={`https://wa.me/${whatsappPhone}?text=${reminderMessage}`} target="_blank" rel="noopener" className="btn btn-primary">
              <MessageCircle size={14} /> Send via WhatsApp
            </a>
          )}
          {contact.email && (
            <a href={`mailto:${contact.email}?subject=${encodeURIComponent('Statement of account')}&body=${reminderMessage}`} className="btn btn-secondary">
              <Mail size={14} /> Email
            </a>
          )}
        </div>
      </header>

      <div className="card grid grid-cols-3 gap-4 max-w-md print:hidden">
        <div className="field"><label>From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div className="field"><label>To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <div className="flex items-end">
          <span className="text-xs font-mono text-text-mute">{lines.length} entries</span>
        </div>
      </div>

      {/* Paper-cream statement surface for print */}
      <div className="bg-paper text-text-paper rounded-xl shadow-2xl p-10 print:shadow-none print:rounded-none print:p-0 print:bg-white">
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="font-serif text-3xl font-bold">Statement</div>
            <div className="text-xs font-mono mt-1" style={{ color: 'rgba(15,21,33,0.55)' }}>{formatDate(from)} → {formatDate(to)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Customer</div>
            <div className="font-semibold mt-0.5">{contact.name}</div>
            {contact.vat_number && <div className="font-mono text-xs">VAT {contact.vat_number}</div>}
          </div>
        </div>

        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(15,21,33,0.18)' }}>
              <th className="text-left py-2 text-[10px] uppercase tracking-widest font-mono w-24" style={{ color: 'rgba(15,21,33,0.55)' }}>Date</th>
              <th className="text-left py-2 text-[10px] uppercase tracking-widest font-mono w-32" style={{ color: 'rgba(15,21,33,0.55)' }}>Doc</th>
              <th className="text-left py-2 text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Description</th>
              <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-24" style={{ color: 'rgba(15,21,33,0.55)' }}>Charge</th>
              <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-24" style={{ color: 'rgba(15,21,33,0.55)' }}>Credit</th>
              <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-28" style={{ color: 'rgba(15,21,33,0.55)' }}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center" style={{ color: 'rgba(15,21,33,0.55)' }}>No activity in this period.</td></tr>
            ) : lines.map((l, i) => (
              <tr key={i} style={{ borderBottom: '1px dashed rgba(15,21,33,0.1)' }}>
                <td className="py-2.5 text-xs">{formatDate(l.date)}</td>
                <td className="py-2.5 text-xs font-mono">{l.link ? <Link to={l.link} className="hover:underline" style={{ color: '#0c8f60' }}>{l.doc}</Link> : l.doc}</td>
                <td className="py-2.5 text-sm">{l.description}</td>
                <td className="py-2.5 text-right text-sm font-mono tabular-nums">{l.charge ? formatMoney(l.charge) : '—'}</td>
                <td className="py-2.5 text-right text-sm font-mono tabular-nums" style={{ color: '#0c8f60' }}>{l.credit ? '−' + formatMoney(l.credit) : '—'}</td>
                <td className="py-2.5 text-right text-sm font-mono tabular-nums font-semibold">{formatMoney(l.running)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="text-right">
            <div className="text-sm" style={{ color: 'rgba(15,21,33,0.65)' }}>Total charged: <span className="font-mono">{formatMoney(totals.charged)}</span></div>
            <div className="text-sm" style={{ color: 'rgba(15,21,33,0.65)' }}>Total paid:   <span className="font-mono">{formatMoney(totals.paid)}</span></div>
            <div className="mt-2 font-serif text-2xl font-bold" style={{ color: totals.balance > 0 ? '#d6a85a' : '#0c8f60' }}>
              Balance: <span className="font-mono tabular-nums">{formatMoney(totals.balance)}</span>
            </div>
          </div>
        </div>
      </div>
      <style>{`@media print { @page { size: A4; margin: 12mm } body { background: white } }`}</style>
    </div>
  );
}
